import { safeLocalStorage } from '@/composables/useSSR'

/**
 * Onboarding intent persistence.
 *
 * When a visitor clicks a "free K8s namespace" CTA and registers, the claim
 * cannot complete until the email is verified. The intent is persisted in
 * localStorage (account-bound, 7-day TTL) so the verify-email landing page
 * can resume the claim flow after verification.
 *
 * SSR-safe: all storage access goes through safeLocalStorage() and no-ops
 * on the server.
 */

const STORAGE_KEY = 'onboardingIntent'
const INTENT_FREE_K8S = 'free-k8s'
const TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

interface StoredIntent {
  intent: string
  accountId: number
  ts: number
}

function readStoredIntent(): StoredIntent | null {
  const storage = safeLocalStorage()
  if (!storage) return null

  const raw = storage.getItem(STORAGE_KEY)
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as StoredIntent).intent === 'string' &&
      typeof (parsed as StoredIntent).accountId === 'number' &&
      typeof (parsed as StoredIntent).ts === 'number'
    ) {
      return parsed as StoredIntent
    }
  } catch {
    // Corrupt JSON — fall through to cleanup
  }
  clearFreeK8sIntent()
  return null
}

/**
 * Persist the free-K8s claim intent for the given account.
 */
export function persistFreeK8sIntent(accountId: number): void {
  const storage = safeLocalStorage()
  if (!storage) return

  const value: StoredIntent = {
    intent: INTENT_FREE_K8S,
    accountId,
    ts: Date.now()
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Storage full / unavailable — intent is best-effort only
  }
}

/**
 * Whether a valid (non-expired, same-account) free-K8s intent is stored.
 * Expired or foreign-account entries are cleaned up and reported as absent.
 */
export function hasFreeK8sIntent(accountId: number): boolean {
  const stored = readStoredIntent()
  if (!stored) return false

  if (
    stored.intent !== INTENT_FREE_K8S ||
    stored.accountId !== accountId ||
    Date.now() - stored.ts > TTL_MS
  ) {
    clearFreeK8sIntent()
    return false
  }
  return true
}

/**
 * Remove any stored onboarding intent.
 */
export function clearFreeK8sIntent(): void {
  const storage = safeLocalStorage()
  if (!storage) return
  try {
    storage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage errors on cleanup
  }
}
